// unitConversionUtils.js
// Unit mismatch detection and conversion helpers for composite builder

// ─── Unit classification ───────────────────────────────────────────────────────

// Maps the dropdown value strings used in BuildNewCompositePage → canonical unit strings.
// Add any new dropdown values here as the UI evolves.
const DROPDOWN_VALUE_MAP = {
  squaremeters:  'm2',
  squaremetre:   'm2',
  squaremeter:   'm2',
  cubicmeters:   'm3',
  cubicmetre:    'm3',
  cubicmeter:    'm3',
  linearmeters:  'lm',
  linealmetre:   'lm',
  linealmeter:   'lm',
  count:         'ea',
  each:          'ea',
  number:        'ea',
  item:          'ea',
  kilogram:      'kg',
  kilograms:     'kg',
  tonne:         't',
  tonnes:        't',
};

/**
 * Normalises a unit string before classification.
 * Handles PascalCase/camelCase dropdown values like 'SquareMeters' or 'CubicMeters'.
 * @param {string} unit
 * @returns {string}
 */
function normaliseUnit(unit) {
  if (!unit) return '';
  const key = unit.trim().toLowerCase().replace(/\s+/g, '');
  return DROPDOWN_VALUE_MAP[key] || unit.trim();
}

const UNIT_GROUPS = {
  AREA:   ['m2', 'm²', 'sqm', 'sq m', 'sq.m', 'square m', 'square metre', 'square meter'],
  VOLUME: ['m3', 'm³', 'cum', 'cu m', 'cu.m', 'cubic m', 'cubic metre', 'cubic meter'],
  LINEAR: ['m', 'lm', 'lin m', 'lineal m', 'linear m', 'rm', 'run m'],
  EACH:   ['ea', 'each', 'no', 'nr', 'item', 'sum', 'ls'],
  WEIGHT: ['kg', 't', 'tonne', 'ton'],
};

/**
 * Returns the group key for a given unit string, or null if unrecognised.
 * Accepts both short-form units ('m2', 'm³') and dropdown values ('SquareMeters').
 * @param {string} unit
 * @returns {'AREA'|'VOLUME'|'LINEAR'|'EACH'|'WEIGHT'|null}
 */
export function classifyUnit(unit) {
  if (!unit) return null;
  const normalised = normaliseUnit(unit).toLowerCase().replace(/s$/, '');
  for (const [group, aliases] of Object.entries(UNIT_GROUPS)) {
    if (aliases.some(a => normalised === a || normalised.startsWith(a))) return group;
  }
  return null;
}

// ─── Mismatch analysis ────────────────────────────────────────────────────────

/**
 * @typedef {Object} MismatchInfo
 * @property {boolean} hasMismatch
 * @property {'area_to_volume'|'volume_to_area'|'area_to_linear'|'linear_to_area'|'other'|null} type
 * @property {string} compositeGroup
 * @property {string} materialGroup
 * @property {string} label
 * @property {string} inputLabel
 * @property {string} inputUnit
 * @property {'factor'|'length'} applyToField
 * @property {function} convert  - (inputValue: number) => calculatorValue: number
 */

/**
 * Analyses the composite output unit vs the material unit and returns mismatch info.
 * @param {string} compositeUnit
 * @param {string} materialUnit
 * @returns {MismatchInfo}
 */
export function analyseMismatch(compositeUnit, materialUnit) {
  // Normalise first so labels use short-form units ('m²') not dropdown values ('SquareMeters')
  const cUnit  = normaliseUnit(compositeUnit);
  const mUnit  = normaliseUnit(materialUnit);
  const cGroup = classifyUnit(cUnit);
  const mGroup = classifyUnit(mUnit);
  // Use normalised strings for all human-readable labels below
  compositeUnit = cUnit;
  materialUnit  = mUnit;

  const base = {
    compositeGroup: cGroup,
    materialGroup: mGroup,
    hasMismatch: false,
    type: null,
    label: '',
    inputLabel: '',
    inputUnit: 'mm',
    applyToField: 'factor',
    convert: (v) => v,
  };

  if (!cGroup || !mGroup || cGroup === mGroup) return base;

  // Area composite (m²) + Volume material (m³)
  if (cGroup === 'AREA' && mGroup === 'VOLUME') {
    return {
      ...base,
      hasMismatch: true,
      type: 'area_to_volume',
      label: `Your composite is measured in ${compositeUnit} but this material is measured in ${materialUnit}. Enter the conversion below to calculate how much material is needed per ${compositeUnit}.`,
      inputLabel: 'Thickness',
      inputUnit: 'mm',
      applyToField: 'factor',
      convert: (mm) => parseFloat((mm / 1000).toFixed(6)),
    };
  }

  // Volume composite (m³) + Area material (m²)
  if (cGroup === 'VOLUME' && mGroup === 'AREA') {
    return {
      ...base,
      hasMismatch: true,
      type: 'volume_to_area',
      label: `Your composite is measured in ${compositeUnit} but this material is measured in ${materialUnit}. Enter the thickness to calculate area of material per ${compositeUnit}.`,
      inputLabel: 'Thickness',
      inputUnit: 'mm',
      applyToField: 'factor',
      convert: (mm) => parseFloat((1000 / mm).toFixed(6)),
    };
  }

  // Area composite (m²) + Linear material (m)
  if (cGroup === 'AREA' && mGroup === 'LINEAR') {
    return {
      ...base,
      hasMismatch: true,
      type: 'area_to_linear',
      label: `Your composite is measured in ${compositeUnit} but this material is measured in ${materialUnit}. Enter the spacing to calculate linear metres per ${compositeUnit}.`,
      inputLabel: 'Spacing',
      inputUnit: 'mm',
      applyToField: 'factor',
      convert: (mm) => parseFloat((1000 / mm).toFixed(6)),
    };
  }

  // Linear composite (m) + Area material (m²)
  if (cGroup === 'LINEAR' && mGroup === 'AREA') {
    return {
      ...base,
      hasMismatch: true,
      type: 'linear_to_area',
      label: `Your composite is measured in ${compositeUnit} but this material is measured in ${materialUnit}. Enter the width to calculate m² per linear metre.`,
      inputLabel: 'Width',
      inputUnit: 'mm',
      applyToField: 'factor',
      convert: (mm) => parseFloat((mm / 1000).toFixed(6)),
    };
  }

  return {
    ...base,
    hasMismatch: true,
    type: 'other',
    label: `Unit mismatch: composite is ${compositeUnit}, material is ${materialUnit}. Manually enter a conversion factor.`,
    inputLabel: 'Factor',
    inputUnit: '',
    applyToField: 'factor',
    convert: (v) => v,
  };
}

// ─── Preset types ─────────────────────────────────────────────────────────────
//
// Three input modes are supported by UnitMismatchAdvisor:
//
//   'thickness'    — user types a value in mm, system converts to metres.
//                    options[] contains mm values.
//                    Used for: concrete, masonry, fill, insulation, render.
//
//   'face_percent' — BRANZ percentage-of-framing-face method for wall framing.
//                    User sets face% (default 36) + stud depth → m³/m².
//                    Formula: (face% / 100) × (depth_mm / 1000)
//                    fallbackOptions[] mirrors the centres-based picker for
//                    users who prefer to work that way instead.
//
//   'factor'       — user picks from pre-calculated options expressed as m³/m²
//                    (volume content ratio). options[] = { label, value }.
//                    Used for: floor/roof framing, engineered wood, steel framing.
//
// The preset's inputMode tells UnitMismatchAdvisor which UI to render.

// ─── Factor presets (volume content ratio or face-percent, not thickness-based) ─

const FACTOR_PRESETS = [
  // ── Softwood / Timber Wall Framing ──────────────────────────────────────────
  // Primary method: BRANZ face-percentage (36% default per BRANZ bulletin).
  // Formula: factor (m³/m²) = (facePercent / 100) × (studDepth / 1000)
  // e.g. 36% × 90 mm = 0.0324 m³/m²
  //
  // Fallback method (user can toggle): framing-centres picker with pre-calculated
  // ratios derived from member geometry (studs + plates + noggins).
  {
    keywords: [
      'softwood', 'pine', 'radiata pine', 'timber framing', 'wall framing',
      'stud framing', 'stud wall', 'framing timber', 'structural timber',
      'mgp10', 'mgp12', 'mgp15', 'h1.2', 'h2', 'h3',
    ],
    inputMode: 'face_percent',
    hint: 'BRANZ standard: 36% framing face per m² of wall area. Adjust percentage and stud depth to suit your design.',
    // ── Face-percent inputs ──
    defaultFacePercent: 36,
    depthOptions: [
      { label: '90 mm',  depth: 90  },
      { label: '140 mm', depth: 140 },
      { label: '190 mm', depth: 190 },
      { label: '240 mm', depth: 240 },
    ],
    defaultDepth: 90,
    // ── Fallback: framing-centres picker (shown when user toggles method) ──
    // Values derived from: (b×d/centres) + plates + noggins per m²
    fallbackOptions: [
      { label: '90×45 @ 600 crs (light)',    value: 0.015 },
      { label: '90×45 @ 450 crs (standard)', value: 0.019 },
      { label: '90×45 @ 400 crs',            value: 0.021 },
      { label: '140×45 @ 600 crs',           value: 0.022 },
      { label: '140×45 @ 450 crs',           value: 0.028 },
      { label: '190×45 @ 600 crs',           value: 0.029 },
      { label: '190×45 @ 450 crs',           value: 0.037 },
      { label: 'LVL / heavy framing',        value: 0.045 },
      { label: 'Portal frame / heavy',       value: 0.060 },
    ],
    fallbackDefaultLabel: '90×45 @ 600 crs (light)',
  },

  // ── Softwood / Timber Floor Framing ──
  {
    keywords: [
      'floor joist', 'floor framing', 'floor bearer', 'joist', 'bearer',
      'timber floor', 'softwood floor',
    ],
    inputMode: 'factor',
    hint: 'Floor framing — m³ content per m² varies with joist size and spacing',
    defaultLabel: '190×45 @ 450 crs (standard)',
    options: [
      { label: '140×45 @ 450 crs (light)',    value: 0.022 },
      { label: '190×45 @ 450 crs (standard)', value: 0.029 },
      { label: '190×45 @ 300 crs',            value: 0.043 },
      { label: '240×45 @ 450 crs',            value: 0.037 },
      { label: '290×45 @ 450 crs',            value: 0.044 },
      { label: 'LVL joists @ 450 crs',        value: 0.040 },
    ],
  },

  // ── Softwood / Timber Roof Framing ──
  {
    keywords: [
      'rafter', 'roof framing', 'roof timber', 'truss', 'ceiling joist',
      'hip rafter', 'purlin', 'timber roof',
    ],
    inputMode: 'factor',
    hint: 'Roof framing — m³ content per m² of roof plan area',
    defaultLabel: '90×45 rafters @ 600 crs',
    options: [
      { label: '90×45 rafters @ 600 crs',         value: 0.018 },
      { label: '140×45 rafters @ 600 crs',         value: 0.026 },
      { label: '190×45 rafters @ 600 crs',         value: 0.035 },
      { label: 'Prefab truss (typical)',            value: 0.022 },
      { label: 'Prefab truss (heavy / long span)',  value: 0.035 },
    ],
  },

  // ── Engineered Wood / CLT / LVL / Glulam ──
  {
    keywords: [
      'lvl', 'laminated veneer lumber', 'glulam', 'glue laminated',
      'engineered wood', 'engineered timber', 'cross laminated', 'clt',
    ],
    inputMode: 'factor',
    hint: 'Engineered wood — for CLT panels the factor equals the panel thickness in metres',
    defaultLabel: 'CLT 90 mm panel',
    options: [
      { label: 'CLT 90 mm panel',       value: 0.090 },
      { label: 'CLT 120 mm panel',      value: 0.120 },
      { label: 'CLT 160 mm panel',      value: 0.160 },
      { label: 'CLT 200 mm panel',      value: 0.200 },
      { label: 'LVL beam (allow)',       value: 0.040 },
      { label: 'Glulam beam (allow)',    value: 0.050 },
    ],
  },

  // ── Light Gauge Steel Framing ──
  {
    keywords: [
      'light gauge steel', 'light steel framing', 'lgsf', 'steel stud',
      'cold formed steel', 'steel framing',
    ],
    inputMode: 'factor',
    hint: 'Light gauge steel framing — volume content per m² of wall',
    defaultLabel: '90 mm steel stud @ 600 crs',
    options: [
      { label: '64 mm steel stud @ 600 crs', value: 0.0008 },
      { label: '90 mm steel stud @ 600 crs', value: 0.0010 },
      { label: '150 mm steel stud @ 600 crs',value: 0.0015 },
    ],
  },
];

// ─── Thickness presets (mm-based) ─────────────────────────────────────────────
// NZS 3101 / AS 3600 / common Australian-NZ construction practice.

const THICKNESS_PRESETS = [
  // ── Concrete – Slabs ──
  { keywords: ['ground slab', 'slab on ground', 'sog', 'ground floor slab'],
    inputMode: 'thickness',
    default: 100, options: [75, 100, 125, 150, 175, 200],
    hint: 'Ground slab — 100 mm typical' },

  { keywords: ['suspended slab', 'topping slab'],
    inputMode: 'thickness',
    default: 150, options: [100, 125, 150, 175, 200, 250],
    hint: 'Suspended slab — 150 mm typical' },

  { keywords: ['raft slab', 'raft foundation', 'raft'],
    inputMode: 'thickness',
    default: 250, options: [200, 250, 300, 350, 400],
    hint: 'Raft slab — 250–300 mm typical' },

  { keywords: ['screed', 'topping'],
    inputMode: 'thickness',
    default: 50, options: [38, 40, 50, 65, 75],
    hint: 'Screed — 50 mm typical' },

  // ── Concrete – Walls ──
  { keywords: ['retaining wall', 'retention wall'],
    inputMode: 'thickness',
    default: 300, options: [200, 250, 300, 350, 400, 500],
    hint: 'Retaining wall — varies with height' },

  { keywords: ['shear wall', 'core wall'],
    inputMode: 'thickness',
    default: 200, options: [150, 175, 200, 250, 300],
    hint: 'Shear / core wall — 200 mm typical' },

  { keywords: ['concrete wall'],
    inputMode: 'thickness',
    default: 200, options: [150, 175, 200, 225, 250, 300],
    hint: 'Concrete wall — 200 mm typical' },

  // ── Concrete – Footings ──
  { keywords: ['strip footing', 'strip foundation', 'continuous footing'],
    inputMode: 'thickness',
    default: 300, options: [200, 250, 300, 400, 500],
    hint: 'Strip footing — 300 mm typical' },

  { keywords: ['pad footing', 'isolated footing', 'column footing'],
    inputMode: 'thickness',
    default: 400, options: [300, 400, 500, 600, 750],
    hint: 'Pad footing — varies with load' },

  { keywords: ['footing', 'foundation'],
    inputMode: 'thickness',
    default: 300, options: [200, 300, 400, 500],
    hint: 'Footing — 300 mm typical' },

  // ── Concrete – Beams / Columns ──
  { keywords: ['beam'],
    inputMode: 'thickness',
    default: 400, options: [300, 350, 400, 500, 600],
    hint: 'Beam depth — varies with span' },

  { keywords: ['column'],
    inputMode: 'thickness',
    default: 300, options: [200, 250, 300, 400, 500, 600],
    hint: 'Column section — varies with load' },

  // ── Fill / Earthworks ──
  { keywords: ['compacted fill', 'engineered fill', 'structural fill'],
    inputMode: 'thickness',
    default: 300, options: [100, 200, 300, 500, 1000],
    hint: 'Compacted fill — varies with design' },

  { keywords: ['sand layer', 'sand bed'],
    inputMode: 'thickness',
    default: 75, options: [50, 75, 100, 150],
    hint: 'Sand layer — 75 mm typical' },

  { keywords: ['gravel base', 'granular base', 'basecourse', 'base course'],
    inputMode: 'thickness',
    default: 150, options: [100, 150, 200, 250, 300],
    hint: 'Basecourse — 150 mm typical' },

  { keywords: ['sub-base', 'subbase', 'sub base'],
    inputMode: 'thickness',
    default: 150, options: [100, 150, 200, 300],
    hint: 'Sub-base — 150 mm typical' },

  { keywords: ['gravel', 'crushed rock'],
    inputMode: 'thickness',
    default: 100, options: [50, 75, 100, 150, 200],
    hint: 'Gravel layer — 100 mm typical' },

  { keywords: ['fill'],
    inputMode: 'thickness',
    default: 300, options: [100, 200, 300, 500, 1000, 2000],
    hint: 'Fill — varies with design' },

  // ── Masonry ──
  { keywords: ['brick veneer', 'clay brick'],
    inputMode: 'thickness',
    default: 110, options: [90, 110, 230],
    hint: 'Brick veneer — 110 mm standard' },

  { keywords: ['cmu', 'concrete block', 'blockwork', 'concrete masonry'],
    inputMode: 'thickness',
    default: 190, options: [90, 140, 190, 240],
    hint: 'Blockwork — 190 mm standard' },

  // ── Insulation ──
  { keywords: ['rigid insulation', 'xps', 'eps', 'polyiso'],
    inputMode: 'thickness',
    default: 50, options: [25, 38, 50, 75, 100],
    hint: 'Rigid insulation — 50 mm typical' },

  { keywords: ['insulation', 'batt'],
    inputMode: 'thickness',
    default: 90, options: [50, 75, 90, 100, 140, 200],
    hint: 'Insulation batts — 90 mm typical' },

  // ── Render / Plaster ──
  { keywords: ['render', 'cement render'],
    inputMode: 'thickness',
    default: 15, options: [10, 12, 15, 20, 25],
    hint: 'Render coat — 15 mm typical' },

  { keywords: ['plaster', 'plasterboard', 'gyprock'],
    inputMode: 'thickness',
    default: 13, options: [10, 13, 16, 19],
    hint: 'Plasterboard — 13 mm standard' },

  // ── Concrete (generic fallback) ──
  { keywords: ['concrete', 'rcc', 'reinforced concrete', 'precast'],
    inputMode: 'thickness',
    default: 150, options: [75, 100, 125, 150, 200, 250, 300, 350, 400],
    hint: 'Concrete element — enter design thickness' },

  // ── Slab (generic fallback) ──
  { keywords: ['slab'],
    inputMode: 'thickness',
    default: 100, options: [75, 100, 125, 150, 175, 200, 250],
    hint: 'Slab — enter design thickness' },
];

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the best matching preset for a material name, or null.
 *
 * Checks FACTOR_PRESETS first (timber, engineered wood, steel framing),
 * then THICKNESS_PRESETS (concrete, masonry, fill).
 *
 * The returned object always has:
 *   inputMode: 'thickness' | 'factor'
 *   hint: string
 *
 * For 'thickness' mode:
 *   default: number (mm)
 *   options: number[]  (mm values for quick-pick chips)
 *
 * For 'factor' mode:
 *   defaultLabel: string  (label of the default option)
 *   options: { label: string, value: number }[]
 *
 * @param {string} materialName
 * @returns {Object|null}
 */
export function getThicknessPreset(materialName) {
  if (!materialName) return null;
  const lower = materialName.toLowerCase();

  for (const preset of FACTOR_PRESETS) {
    if (preset.keywords.some(kw => lower.includes(kw))) return preset;
  }

  for (const preset of THICKNESS_PRESETS) {
    if (preset.keywords.some(kw => lower.includes(kw))) return preset;
  }

  return null;
}

/**
 * Formats a factor value for display (strips trailing zeros up to 4 dp).
 * @param {number} value
 * @returns {string}
 */
export function formatFactor(value) {
  if (value === 0) return '0';
  return parseFloat(value.toFixed(4)).toString();
}